/**
 * Shared data hooks for the Salvation Team console: lookups (one call for every lookup), settings, campus teams and
 * team members, each with the select options the pages need.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useSalvationAdminService,
  useSalvationLookupsService,
  type SalvationLookupItem,
  type SalvationLookupsView,
} from '@actschurch/ccms-sdk';
import { salvationKeys } from './salvationUtils';

export interface SelectOption {
  value: string;
  label: string;
}

/** Lookup items as select options (active ones, plus the current value even when it was switched off). */
export function lookupOptions(items: SalvationLookupItem[] | undefined, keep?: number | null): SelectOption[] {
  return (items ?? []).filter((i) => i.isActive || i.id === keep).map((i) => ({ value: String(i.id), label: i.name }));
}

/** id → item for one lookup list. */
export function lookupMap<T extends SalvationLookupItem>(items: T[] | undefined): Map<number, T> {
  return new Map((items ?? []).map((i) => [i.id, i]));
}

export function useSalvationLookups() {
  const lookups = useSalvationLookupsService();
  const query = useQuery({ queryKey: salvationKeys.lookups(), queryFn: () => lookups.getLookups(), staleTime: 5 * 60_000, retry: 1 });
  const maps = useMemo(() => buildMaps(query.data), [query.data]);
  return { ...query, ...maps };
}

function buildMaps(data: SalvationLookupsView | undefined) {
  return {
    statuses: lookupMap(data?.statuses),
    followUpTypes: lookupMap(data?.followUpTypes),
    reasons: lookupMap(data?.responsibilityReasons),
    decisionSources: lookupMap(data?.decisionSources),
    channels: lookupMap(data?.intakeChannels),
  };
}

export function useSalvationSettings(enabled = true) {
  const admin = useSalvationAdminService();
  return useQuery({ queryKey: salvationKeys.settings(), queryFn: () => admin.getSettings(), staleTime: 60_000, enabled });
}

/** The campus teams this user may see (leaders: the ones they lead). */
export function useSalvationTeams() {
  const admin = useSalvationAdminService();
  const query = useQuery({ queryKey: salvationKeys.teams(), queryFn: () => admin.getTeams(), staleTime: 60_000 });
  const options = useMemo<SelectOption[]>(
    () => (query.data ?? []).filter((t) => t.isActive).map((t) => ({ value: String(t.teamID), label: t.name })),
    [query.data]
  );
  return { ...query, options };
}

/** A team's members (active first in rank order), with Lead / Backup select options for the active ones. */
export function useSalvationTeamMembers(teamId: number | null | undefined) {
  const admin = useSalvationAdminService();
  const query = useQuery({
    queryKey: salvationKeys.members(teamId ?? 0),
    queryFn: () => admin.getTeamMembers(teamId as number),
    enabled: !!teamId,
    staleTime: 30_000,
  });
  const options = useMemo<SelectOption[]>(
    () =>
      (query.data ?? [])
        .filter((m) => m.isActive)
        .map((m) => ({ value: String(m.teamMemberID), label: `${m.position}. ${m.displayName}${m.pool === 'lead' ? ' (Lead pool)' : ''}` })),
    [query.data]
  );
  return { ...query, options };
}
